import { ref, computed } from 'vue';
import { format, parseISO } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

interface ProfessionalData {
  id: number;
  name: string;
  location: string;
  avatar?: string;
}

// Map common location names to IANA timezones
const LOCATION_TO_TIMEZONE: Record<string, string> = {
  // Chile
  santiago: 'America/Santiago',
  chile: 'America/Santiago',
  // Argentina
  'buenos aires': 'America/Buenos_Aires',
  argentina: 'America/Buenos_Aires',
  // Perú
  lima: 'America/Lima',
  peru: 'America/Lima',
  perú: 'America/Lima',
  // Colombia
  bogota: 'America/Bogota',
  bogotá: 'America/Bogota',
  colombia: 'America/Bogota',
  // Venezuela
  caracas: 'America/Caracas',
  venezuela: 'America/Caracas',
  // Bolivia
  'la paz': 'America/La_Paz',
  bolivia: 'America/La_Paz',
  // Paraguay
  asuncion: 'America/Asuncion',
  asunción: 'America/Asuncion',
  paraguay: 'America/Asuncion',
  // Uruguay
  montevideo: 'America/Montevideo',
  uruguay: 'America/Montevideo',
  // Ecuador
  quito: 'America/Guayaquil',
  ecuador: 'America/Guayaquil',
  // México
  mexico: 'America/Mexico_City',
  méxico: 'America/Mexico_City',
  'mexico city': 'America/Mexico_City',
  'ciudad de mexico': 'America/Mexico_City',
  // USA
  'new york': 'America/New_York',
  chicago: 'America/Chicago',
  denver: 'America/Denver',
  'los angeles': 'America/Los_Angeles',
  // España
  madrid: 'Europe/Madrid',
  españa: 'Europe/Madrid',
  spain: 'Europe/Madrid',
  // Online/Remote
  online: 'UTC',
  remoto: 'UTC',
  remote: 'UTC',
};

// Normalize location to IANA timezone
function normalizeTimezone(location: string | undefined | null): string {
  if (!location) return 'UTC';

  const normalized = location.toLowerCase().trim();

  // Check if it's already a valid IANA timezone
  try {
    Intl.DateTimeFormat(undefined, { timeZone: location });
    return location;
  } catch {
    // Not a valid IANA timezone, try to map it
  }

  // Try to map from common location names
  const mapped = LOCATION_TO_TIMEZONE[normalized];
  if (mapped) return mapped;

  // Try partial matching (e.g., "Santiago, Chile" -> "santiago")
  for (const [key, value] of Object.entries(LOCATION_TO_TIMEZONE)) {
    if (normalized.includes(key)) return value;
  }

  return 'UTC';
}

// Country-only labels (no city names) for the raw HTML / visible badges.
// Kept separate from the timezone green-names map used in scheduling screens.
const LOCATION_LABELS: Record<string, string> = {
  'America/Santiago': 'Chile',
  'America/Argentina/Buenos_Aires': 'Argentina',
  'America/Lima': 'Perú',
  'America/Bogota': 'Colombia',
  'America/Caracas': 'Venezuela',
  'America/La_Paz': 'Bolivia',
  'America/Asuncion': 'Paraguay',
  'America/Montevideo': 'Uruguay',
  'America/Guayaquil': 'Ecuador',
  'America/Mexico_City': 'México',
  'America/New_York': 'USA',
  'America/Chicago': 'USA',
  'America/Denver': 'USA',
  'America/Los_Angeles': 'USA',
  'America/Toronto': 'Canadá',
  'America/Vancouver': 'Canadá',
  'Europe/Madrid': 'España',
  'Europe/Paris': 'Francia',
  'Europe/Berlin': 'Alemania',
  'Europe/London': 'Reino Unido',
  'Europe/Rome': 'Italia',
};

// Visible short country label for a professional location value (IANA or friendly name).
export function locationLabel(location: string | undefined | null): string {
  const tz = normalizeTimezone(location);
  return LOCATION_LABELS[tz] || tz;
}

export function useTimezone() {
  const { locale } = useI18n();

  const professionalData = ref<ProfessionalData | null>(null);
  const loading = ref(false);

  const dateLocale = computed(() => (locale.value === 'es' ? es : enUS));

  // Get professional timezone (default to UTC if not set)
  const timezone = computed(() => {
    return normalizeTimezone(professionalData.value?.location);
  });

  // Fetch professional data including timezone
  const fetchProfessionalData = async (professionalId: number) => {
    if (!professionalId) return;

    loading.value = true;
    try {
      const response = await axios.get(`/api/v1/professionals/${professionalId}`);
      professionalData.value = response.data;
    } catch (error) {
      console.error('Error loading professional data:', error);
      professionalData.value = null;
    } finally {
      loading.value = false;
    }
  };

  // Convert UTC datetime to professional's timezone and format
  const formatInTimezone = (datetime: string | Date, formatStr: string): string => {
    try {
      const date = typeof datetime === 'string' ? new Date(datetime) : datetime;
      const tz = timezone.value;

      if (tz === 'UTC') {
        // Fallback to date-fns if UTC
        return format(parseISO(date.toISOString()), formatStr, { locale: dateLocale.value });
      }

      // Convert to professional's timezone using Intl.DateTimeFormat
      const options: Intl.DateTimeFormatOptions = {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      };

      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(date);

      const year = parts.find((p) => p.type === 'year')?.value;
      const month = parts.find((p) => p.type === 'month')?.value;
      const day = parts.find((p) => p.type === 'day')?.value;
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;

      // Return formatted based on format string
      if (formatStr === 'dd/MM/yyyy HH:mm') {
        return `${day}/${month}/${year} ${hour}:${minute}`;
      }
      if (formatStr === 'HH:mm') {
        return `${hour}:${minute}`;
      }
      if (formatStr === 'dd/MM/yyyy') {
        return `${day}/${month}/${year}`;
      }

      // Default: parse and use date-fns format on converted date
      const tzDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
      return format(tzDate, formatStr, { locale: dateLocale.value });
    } catch (e) {
      console.error('Error formatting date in timezone:', e);
      // Fallback: use original date with date-fns
      const date = typeof datetime === 'string' ? parseISO(datetime) : datetime;
      return format(date, formatStr, { locale: dateLocale.value });
    }
  };

  // Format date only (for dates)
  const formatDate = (dateStr: string): string => {
    try {
      const date = parseISO(dateStr);
      return format(date, "EEEE d 'de' MMMM", { locale: dateLocale.value });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr;
    }
  };

  // Get timezone display name
  const timezoneDisplayName = computed(() => {
    const tz = timezone.value;
    if (tz === 'UTC') return '';

    const names: Record<string, string> = {
      'America/Santiago': 'Hora Chile',
      'America/Buenos_Aires': 'Hora Argentina',
      'America/Lima': 'Hora Perú',
      'America/Bogota': 'Hora Colombia',
      'America/Caracas': 'Hora Venezuela',
      'America/La_Paz': 'Hora Bolivia',
      'America/Asuncion': 'Hora Paraguay',
      'America/Montevideo': 'Hora Uruguay',
      'America/Quito': 'Hora Ecuador',
      'America/Mexico_City': 'Hora México',
      'America/New_York': 'Hora Este (USA)',
      'America/Chicago': 'Hora Centro (USA)',
      'America/Denver': 'Hora Montaña (USA)',
      'America/Los_Angeles': 'Hora Pacífico (USA)',
      'America/Toronto': 'Hora Este (Canadá)',
      'America/Vancouver': 'Hora Pacífico (Canadá)',
      'Europe/Madrid': 'Hora España',
      'Europe/Paris': 'Hora Francia',
      'Europe/Berlin': 'Hora Alemania',
      'Europe/London': 'Hora Reino Unido',
      'Europe/Rome': 'Hora Italia',
    };

    return names[tz] || tz;
  });

  return {
    timezone,
    timezoneDisplayName,
    professionalData,
    loading,
    fetchProfessionalData,
    formatInTimezone,
    formatDate,
    normalizeTimezone,
  };
}

// Export standalone function for use outside the composable
export { normalizeTimezone };
